import { httpRequest } from '@/utils/umiRequest';
import { formatTableParams, formatTableResponse, isPro, isStaging } from '@/utils/utils';
import { BASEURL } from '@/utils/enums';
import { getGlobalConfigByModule } from '../configs';

export interface saasDisputeRequest {
  currentPage: number;
  lists: Record<string, any>[];
  pageSize: number;
  total: number;
  totalPage: number;
}

export interface Datum {
  canned_response: CannedResponse[];
  created_at: string;
  freshdesk_id: number;
  id: number;
  name: string;
  personal: number;
  responses_count: number;
  type: number;
  updated_at: string;
  [property: string]: any;
}

export interface CannedResponse {
  amount: string;
  attachments: null;
  content_html: string;
  created_at: string;
  created_user_id: number;
  currency: string;
  folder_id: number;
  folder_type: number;
  freshdesk_id: number;
  group_ids: null;
  id: number;
  negative_title: string;
  positive_title: string;
  scope: number;
  title: string;
  type: string;
  updated_at: string;
  visibility: number;
  [property: string]: any;
}

/** 获取分组-new */
export async function getAllSessionsTable(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<saasDisputeRequest>('/chat-ticket', params, options);
}

//获取分组回复
export async function getFolderResponse(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.get<Datum[]>('/canned-response/folder-responses', params, options);
}

//获取分组回复
export async function getFolderResponseCancelable(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return httpRequest.cancelable.get('/canned-response/folder-responses', params, options);
}

//预设话术总数修改
export async function setUseTotal(params: Record<string, any>, options?: Record<string, any>) {
  return httpRequest.cancelable.post('/canned-response/use-total', params, options);
}

// 获取大数据订单详情
export function getOrderDetail(orderId: string, options?: Record<string, any>) {
  return httpRequest.get<any>(`/lfd/ppquery/order/${orderId}`, options);
}

// 获取大数据订单详情（可取消）
export function getOrderDetailCancelable(orderId: string, options?: Record<string, any>) {
  return httpRequest.cancelable.get<any>(`/lfd/ppquery/order/${orderId}`, options);
}

// 争议工单详情
export async function getSaasDisputeDetail(id: string, options?: Record<string, any>) {
  return await httpRequest.get<any>(`/chat-ticket/${id}`, {}, options);
}

// 聊天历史消息项
export interface ChatMessageItem {
  id: number;
  message_status: number;
  chat_ticket_id: number;
  message_content: string;
  sender_name: string;
  sender_id: number | null;
  type: number;
  created_at: string;
  updated_at: string;
}

// 聊天历史消息（游标分页）
export interface ChatMessageData {
  lists: ChatMessageItem[];
  next_cursor: number | null;
  has_more: boolean;
}

// 获取聊天消息（不传参数获取最新一页，传 next_cursor 加载更早的历史消息）
// NOTE 该接口走 /api/chat 前缀，需覆盖全局默认的 /api/admin baseURL
export async function getChatDetail(
  id: string,
  params?: { next_cursor?: number | null },
  options?: Record<string, any>,
) {
  return await httpRequest.get<ChatMessageData>(
    `/chat-ticket/${id}/messages`,
    params ?? { },
    options,
  );
}

// Centrifugo token 载荷：connection_token 为连接 JWT，
// subscriptions 为 频道名 -> 该频道的订阅 JWT（如 chat:77 工单频道）
export interface CentrifugoTokenData {
  connection_token: string;
  subscriptions: Record<string, string>;
}

// Centrifugo token（GET /chat-ticket/{id}/centrifugo-token）
// NOTE 该接口走 /api/admin 前缀；返回结构可能是 { data: {...} } 包裹，也可能直接平铺在顶层，
// 类型上两种都兼容，由调用处归一化
export interface CentrifugoTokenResponse extends CentrifugoTokenData {
  data?: CentrifugoTokenData;
}

// 获取 Centrifugo 连接 token 与各频道订阅 token
export async function getCentrifugoToken(id: string, options?: Record<string, any>) {
  return await httpRequest.get<CentrifugoTokenResponse>(
    `/chat-ticket/${id}/centrifugo-token`,
    {},
    options,
  );
}

// 发送消息入参（type 为发送者身份：0 客户 / 1 客服）
export interface SendChatMessageParams {
  type: number;
  sender_name: string;
  content: string;
}

// 发送消息（返回新创建的消息）
// NOTE 该接口走 /api/chat 前缀，需覆盖全局默认的 /api/admin baseURL
export async function sendChatMessage(
  id: string,
  params: SendChatMessageParams,
  options?: Record<string, any>,
) {
  return await httpRequest.post<ChatMessageItem>(
    `/chat-ticket/${id}/messages`,
    params,
    options,
  );
}

// 获取物流信息
export async function getTrackExpressRecord(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>('/lfd/track/express/record', params, options);
}

// 物流虚拟单号查询
export async function getVmExpressRecord(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.get<any>(`/lfd/track/vm-express/record`, params, options);
}

// 新增争议工单标签
export async function createSaasDisputeTag(
  id: string,
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/saas-dispute/${id}/tags`, params, options);
}

// 设置争议工单标签
export async function setSaasDisputeTag(
  id: string,
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(`/saas-dispute/${id}/tags`, params, options);
}

// 移除争议工单标签
export async function removeSaasDisputeTag(
  id: string,
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.delete<any>(`/saas-dispute/${id}/tags`, params, options);
}

// 获取标签
export async function getSaasDisputeTag(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.get<any>(`/tags`, params, options);
}

// 获取咨询原因
export async function getDisputeReason(options?: Record<string, any>) {
  return await httpRequest.get<any>(`/dispute-reason`, {}, options);
}

// 设置备注
export async function setRemarks(
  id: string,
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(`/chat-ticket/${id}/remark`, params, options);
}

// 修改订单号
export async function updateOrderNumber(
  id: string,
  params: { order_id: string },
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(`/saas-dispute/${id}/orders`, params, options);
}

// 设置备注
export async function messageRevoke(uuid: string, options?: Record<string, any>) {
  return await httpRequest.get<any>(`/saas-dispute/revoke/${uuid}`, {}, options);
}

// 获取智能推荐列表数据
export async function getIntelligentReplyListData(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/chat/qdesksaas/recommendations`, params, {
    ...options,
    headers: {
      isRecommendations: true,
    },
  });
}

// 采纳计数
export async function updateAdoptionCount(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/chat/qdesksaas/adoption`, params, options);
}

// 上传采纳信息
export async function updateAdoptionInfo(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/chat/qdesksaas/adoption-record/store`, params, options);
}

// 新增推荐
export async function createRecommendations(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/chat/qdesksaas/recommendations/store`, params, options);
}

// 变更采纳状态
export async function changeRecommendations(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(
    `/saas-dispute/change-adoption/${params.id}`,
    { is_adoption: params.status },
    options,
  );
}

// 获取采纳状态
export async function getRecommendationStatus(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.get<any>(`/saas-dispute/adoption-status/${params.id}`, {}, options);
}

export async function toTranslate(message: any[], target_lang?: string, source?: number) {
  return await httpRequest.post<any>(
    `/util/translate`,
    {
      target_language: target_lang,
      text: message,
      source,
    },
    {},
  );
}

// 历史消息翻译结果保存
export async function saveSaasTranslateMessage(
  message_id: string,
  data: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/saas-dispute/message/${message_id}`, data, options);
}

// 历史消息翻译结果保存
export async function saveEmailTranslateMessage(
  message_id: string,
  data: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/email-ticket/email/${message_id}`, data, options);
}
// 批量关闭
export async function batchCloseDispute(
  params: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.put<any>(`/saas-dispute/set-status-close`, params, options);
}

// 工单标题翻译结果保存
export async function saveSubjectTranslateMessage(
  ticket_id: number,
  data: Record<string, any>,
  options?: Record<string, any>,
) {
  return await httpRequest.post<any>(`/email-ticket/translate/${ticket_id}`, data, options);
}

/**
 * 获取SaaS工单模块的配置
 * @param params
 * @param options
 * @param cancelable 可否取消
 * @returns
 */
export async function getSaasDisputeTicketConfig(
  options?: Record<string, any>,
  cancelable = false,
) {
  return await getGlobalConfigByModule('saas-ticket', options, cancelable);
}
